import { useEffect } from 'react';

interface OpenGraphProps {
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  url?: string;
  type?: 'website' | 'article';
  siteName?: string;
}

interface HreflangEntry {
  hreflang: string;
  href: string;
}

interface PageSEOProps {
  pageId: string;
  schema?: object | object[];
  canonical?: string;
  title?: string;
  description?: string;
  openGraph?: OpenGraphProps;
  hreflang?: HreflangEntry[];
}

const CANONICAL_TAG_ID = 'page-seo-canonical';
const TITLE_TAG_ID = 'page-seo-title';
const DESC_TAG_ID = 'page-seo-description';
const OG_TAG_PREFIX = 'page-seo-og-';
const HREFLANG_TAG_PREFIX = 'page-seo-hreflang-';

// Helper: upsert a <meta> tag identified by a unique id
const upsertMeta = (id: string, attrs: Record<string, string>): HTMLMetaElement => {
  let el = document.getElementById(id) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.id = id;
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  return el;
};

const PageSEO: React.FC<PageSEOProps> = ({
  pageId,
  schema,
  canonical,
  title,
  description,
  openGraph,
  hreflang,
}) => {
  // ── Schema injection ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!schema) return;

    const id = `schema-ld-${pageId}`;
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(Array.isArray(schema) ? schema : schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [schema, pageId]);

  // ── Canonical link ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canonical) return;

    const existing = document.getElementById(CANONICAL_TAG_ID);
    if (existing) existing.remove();

    const link = document.createElement('link');
    link.id = CANONICAL_TAG_ID;
    link.rel = 'canonical';
    link.href = canonical;
    document.head.appendChild(link);

    return () => {
      const el = document.getElementById(CANONICAL_TAG_ID);
      if (el) el.remove();
    };
  }, [canonical]);

  // ── <title> override ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!title) return;

    const originalTitle = document.title;

    const existing = document.getElementById(TITLE_TAG_ID);
    if (existing) existing.remove();

    document.title = title;

    const titleEl = document.createElement('title');
    titleEl.id = TITLE_TAG_ID;
    titleEl.textContent = title;
    document.head.appendChild(titleEl);

    return () => {
      document.title = originalTitle;
      const el = document.getElementById(TITLE_TAG_ID);
      if (el) el.remove();
    };
  }, [title]);

  // ── Meta description override ─────────────────────────────────────────────
  useEffect(() => {
    if (!description) return;

    const staticMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]:not([id])'
    );
    const originalContent = staticMeta?.content ?? '';

    if (staticMeta) {
      staticMeta.content = description;
    } else {
      const existing = document.getElementById(DESC_TAG_ID);
      if (existing) existing.remove();

      const meta = document.createElement('meta');
      meta.id = DESC_TAG_ID;
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }

    return () => {
      if (staticMeta) {
        staticMeta.content = originalContent;
      } else {
        const el = document.getElementById(DESC_TAG_ID);
        if (el) el.remove();
      }
    };
  }, [description]);

  // ── Open Graph + Twitter Card tags ───────────────────────────────────────
  useEffect(() => {
    if (!openGraph) return;

    const {
      title: ogTitle,
      description: ogDesc,
      image,
      imageAlt,
      imageWidth,
      imageHeight,
      url,
      type = 'website',
      siteName,
    } = openGraph;

    // Build the list of tags to inject
    const tags: Array<{ id: string; attrs: Record<string, string> }> = [];

    const add = (id: string, property: string, content: string) => {
      tags.push({ id: `${OG_TAG_PREFIX}${id}`, attrs: { property, content } });
    };
    const addTwitter = (id: string, name: string, content: string) => {
      tags.push({ id: `${OG_TAG_PREFIX}tw-${id}`, attrs: { name, content } });
    };

    // Core OG tags
    add('type',        'og:type',        type);
    if (ogTitle)      add('title',       'og:title',       ogTitle);
    if (ogDesc)       add('description', 'og:description', ogDesc);
    if (url)          add('url',         'og:url',         url);
    if (siteName)     add('site_name',   'og:site_name',   siteName);

    // OG image tags
    if (image) {
      add('image',          'og:image',          image);
      add('image-secure',   'og:image:secure_url', image);
      add('image-type',     'og:image:type',     'image/jpeg');
      if (imageWidth)  add('image-width',  'og:image:width',  String(imageWidth));
      if (imageHeight) add('image-height', 'og:image:height', String(imageHeight));
      if (imageAlt)    add('image-alt',    'og:image:alt',    imageAlt);
    }

    // Twitter Card tags (iMessage, Slack, and Twitter all read these)
    addTwitter('card',        'twitter:card',        image ? 'summary_large_image' : 'summary');
    if (ogTitle)  addTwitter('title',       'twitter:title',       ogTitle);
    if (ogDesc)   addTwitter('description', 'twitter:description', ogDesc);
    if (image)    addTwitter('image',       'twitter:image',       image);
    if (imageAlt) addTwitter('image-alt',   'twitter:image:alt',   imageAlt);

    // Inject all tags
    tags.forEach(({ id, attrs }) => upsertMeta(id, attrs));

    return () => {
      tags.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [openGraph]);

  // ── Hreflang links ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hreflang || hreflang.length === 0) return;

    const injected: HTMLLinkElement[] = [];

    hreflang.forEach(({ hreflang: lang, href }) => {
      const id = `${HREFLANG_TAG_PREFIX}${pageId}-${lang}`;
      // Remove any stale tag with this id first
      const stale = document.getElementById(id);
      if (stale) stale.remove();

      const link = document.createElement('link');
      link.id = id;
      link.rel = 'alternate';
      link.setAttribute('hreflang', lang);
      link.href = href;
      document.head.appendChild(link);
      injected.push(link);
    });

    return () => {
      injected.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [hreflang, pageId]);

  return null;
};

export default PageSEO;
